import {Component, Input, OnInit, ViewChild, ViewContainerRef} from '@angular/core';
import {
    ConnectionType,
    DataType,
    EnergySignatureResult,
    Location,
    TimeResolution
} from '@flowmaps-com/flowmaps-typescriptmodels';
import {AppContext} from '../../../app-context';
import {ChartDataProvider} from '../../../utils/chart-data-provider';
import {ChartDatasetExtended, MeasurementData, MeasurementDataset} from '../base-measurement-chart';
import {ChartModalOptions, ChartUtilsService} from '../chart-utils.service';
import {Handler} from '../../../common/handler';
import {View} from '../../../common/view';
import {
    DataQueryFilters,
    MeasurementsHandlerOpenModalCommand
} from '../../measurements-component/measurements-handler.component';
import {TooltipItem} from "chart.js";
import {TranslateDirective} from "../../../common/utils/translate.directive";
import {ChartOptions, ChartOptionType} from "../../dashboard/dashboard.types";
import {lodash} from "../../../common/utils";
import moment from "moment";

const LIMITED_MEASUREMENT_DATA_SURVEYOR_CODE = "8f4d2bfb-169d-4983-b042-b235d3a36d8f";

export function toEnergyProfileScatterPoints(
    points: {
        temperature?: number;
        consumption?: number;
        timestamp?: string;
        sourceResolution?: TimeResolution;
        average?: boolean;
        estimated?: boolean;
    }[] = []
) {
    return points.map(point => ({
        x: Number(point.temperature),
        y: Number(point.consumption),
        label: point.timestamp,
        sourceResolution: point.sourceResolution,
        average: !!point.average,
        estimated: !!point.estimated
    })).filter(point => Number.isFinite(point.x) && Number.isFinite(point.y));
}

type EnergyProfileScatterPoint = ReturnType<typeof toEnergyProfileScatterPoints>[number];

export function compactEnergyProfileValue(value: number): number {
    return Math.round(value * 10) / 10;
}

export function hourlyEnergyProfileOptionsDisabled(
    optionsDisabled: boolean,
    hourlyDataAvailable: boolean | null
): boolean {
    return optionsDisabled || hourlyDataAvailable === false;
}

@Component({
    selector: 'app-energy-profile-scatter-chart',
    templateUrl: './energy-profile-scatter-chart.component.html'
})
@Handler()
export class EnergyProfileScatterChartComponent extends View implements OnInit {
    @Input() showInReport: boolean;
    @Input() location: Location;
    @ViewChild('container', {read: ViewContainerRef}) container: ViewContainerRef;

    chartDataProvider = new ChartDataProvider();
    modalOptions: ChartModalOptions = {fullScreen: false};
    options: ChartOptions;
    private uniqueId = lodash.uniqueId("energy-profile-scatter-");
    pluginOptions = {
        tooltip: {
            callbacks: {
                title: (items: TooltipItem<'scatter'>[]) => {
                    const point = items[0]?.raw as EnergyProfileScatterPoint;
                    return this.formatTooltipTitle(point);
                },
                label: (item: TooltipItem<'scatter'>) => {
                    const point = item.raw as EnergyProfileScatterPoint;
                    const unit = this.displayResolution === TimeResolution.hour ? 'm³/hour' : 'm³/day';
                    return `${this.formatValue(point.y)} ${this.translate(unit)} · ${this.formatValue(item.parsed.x)} °C`;
                },
                afterLabel: (item: TooltipItem<'scatter'>) => {
                    const point = item.raw as EnergyProfileScatterPoint;
                    if (point.estimated) {
                        return this.translate('calculated from monthly data');
                    }
                    return point.average && point.sourceResolution === TimeResolution.month
                        ? this.translate('month average') : '';
                }
            }
        },
        legend: {
            labels: {
                filter: (item) => !!item.text && item.text !== this.translate('trend line')
            }
        }
    };
    stateMessage = '';
    openingHoursAvailable = false;
    hourlyDataAvailable: boolean | null = null;
    estimatedDataVisible = false;
    displayResolution = TimeResolution.day;
    xAxisTitle = this.translate('outdoor temperature (°c)');
    scales: any = {
        x: {},
        y: {min: 0, suggestedMin: 0}
    };
    filters: DataQueryFilters;
    private lastResult: EnergySignatureResult;

    ngOnInit(): void {
        this.subscribeTo("getChartOptions", ChartOptionType.EnergyProfile).subscribe((opts: ChartOptions) => {
            this.options = lodash.merge({
                showOpeningHoursSplit: false,
                showWeekendWeekdaySplit: false,
                showHourlyEnergyProfile: false
            }, opts || {});
            this.refreshChart();
        });
        this.subscribeTo('getChartDataQueryFilters').subscribe((filters: DataQueryFilters) => {
            this.filters = filters;
            this.loadData();
        });
        this.refreshOpeningHoursAvailability();
    }

    @Input()
    set data(d: ChartModalOptions) {
        this.modalOptions = d;
        if (d && (d as any).location) {
            this.location = (d as any).location;
            this.refreshOpeningHoursAvailability();
        }
    }

    openModal() {
        const options = this.options || {
            showOpeningHoursSplit: false,
            showWeekendWeekdaySplit: false,
            showHourlyEnergyProfile: false
        };
        this.sendCommandAndForget("openChartInModal", <MeasurementsHandlerOpenModalCommand>{
            component: EnergyProfileScatterChartComponent,
            chartOptions: {
                [ChartOptionType.EnergyProfile]: options
            },
            chartModalData: {
                ...this.modalOptions,
                location: this.location
            },
            container: this.container,
            modalClasses: "modal-dialog-centered modal-xl d-flex align-items-stretch"
        });
    }

    closeModal = () => this.sendCommandAndForget("closeModal");

    getInputId = (id: string) => `${this.uniqueId}-${id}`;

    get hourlyOptionsDisabled(): boolean {
        return hourlyEnergyProfileOptionsDisabled(this.optionsDisabled, this.hourlyDataAvailable);
    }

    get openingHoursSplitDisabled(): boolean {
        return this.hourlyOptionsDisabled || !this.openingHoursAvailable;
    }

    get showOpeningHoursSplit(): boolean {
        return !this.optionsDisabled && this.hourlyDataAvailable !== false
            && this.openingHoursAvailable && !!this.options?.showOpeningHoursSplit;
    }

    get showWeekendWeekdaySplit(): boolean {
        return !this.optionsDisabled && !!this.options?.showWeekendWeekdaySplit;
    }

    get optionsDisabled(): boolean {
        return (this.location?.connections || []).some(connection =>
            [ConnectionType.Gas, ConnectionType.Heat].includes(connection.info?.connectionType as ConnectionType)
            && connection.info?.surveyor?.code === LIMITED_MEASUREMENT_DATA_SURVEYOR_CODE);
    }

    get showHourlyEnergyProfile(): boolean {
        return !this.optionsDisabled && this.hourlyDataAvailable !== false
            && !!this.options?.showHourlyEnergyProfile;
    }

    get usesHourlyResolution(): boolean {
        return this.showHourlyEnergyProfile || this.showOpeningHoursSplit;
    }

    get yAxisTitle(): string {
        return this.translate(this.displayResolution === TimeResolution.hour
            ? 'consumption (m³/h)' : 'consumption (m³/day)');
    }

    get chartTitleKey(): string {
        return ChartUtilsService.getEnergyProfileChartTitleKey(this.location);
    }

    private loadData() {
        this.stateMessage = '';
        if (!this.filters?.timeRange || !this.location?.locationId) {
            this.stateMessage = 'No location selected';
            return;
        }
        const requestedResolution = this.usesHourlyResolution ? TimeResolution.hour : TimeResolution.day;
        this.sendQuery('com.flowmaps.api.measurements.GetEnergySignature', {
            timeRange: {start: this.filters.timeRange.start, end: this.filters.timeRange.end},
            locationId: this.location?.locationId,
            resolution: requestedResolution,
            unrounded: true
        }).subscribe((result: EnergySignatureResult) => {
            const signatureResult = result as EnergySignatureResult & {
                resolution?: TimeResolution;
                hourlyDataAvailable?: boolean;
            };
            this.displayResolution = signatureResult.resolution || requestedResolution;
            if (requestedResolution === TimeResolution.hour && signatureResult.hourlyDataAvailable === false) {
                this.hourlyDataAvailable = false;
                this.options.showHourlyEnergyProfile = false;
                this.options.showOpeningHoursSplit = false;
            } else if (signatureResult.hourlyDataAvailable === true) {
                this.hourlyDataAvailable = true;
            }
            this.lastResult = result;
            this.updateChart(result);
        });
    }

    refreshChart = () => {
        if (this.lastResult) {
            this.updateChart(this.lastResult);
        }
    }

    showOpeningHoursSplitChange(showOpeningHoursSplit: boolean) {
        if (this.optionsDisabled || this.hourlyDataAvailable === false || !this.openingHoursAvailable) {
            return;
        }
        this.options = this.options || {showOpeningHoursSplit: false, showWeekendWeekdaySplit: false, showHourlyEnergyProfile: false};
        this.options.showOpeningHoursSplit = showOpeningHoursSplit;
        if (showOpeningHoursSplit) {
            this.options.showWeekendWeekdaySplit = false;
        }
        this.loadData();
    }

    showWeekendWeekdaySplitChange(showWeekendWeekdaySplit: boolean) {
        if (this.optionsDisabled) {
            return;
        }
        this.options = this.options || {showOpeningHoursSplit: false, showWeekendWeekdaySplit: false, showHourlyEnergyProfile: false};
        this.options.showWeekendWeekdaySplit = showWeekendWeekdaySplit;
        if (showWeekendWeekdaySplit) {
            this.options.showOpeningHoursSplit = false;
        }
        this.refreshChart();
    }

    showHourlyEnergyProfileChange(showHourlyEnergyProfile: boolean) {
        if (this.optionsDisabled || this.hourlyDataAvailable === false) {
            return;
        }
        this.options = this.options || {showOpeningHoursSplit: false, showWeekendWeekdaySplit: false, showHourlyEnergyProfile: false};
        this.options.showHourlyEnergyProfile = showHourlyEnergyProfile;
        this.loadData();
    }

    private updateChart(result: EnergySignatureResult) {
        this.estimatedDataVisible = [
            ...((result.openingHours || []) as any[]),
            ...((result.outsideOpeningHours || []) as any[])
        ].some(point => !!point.estimated);
        const datasets = this.datasetsForLocation(result);
        const value: MeasurementData = {
            datasets: datasets.map(d => ({
                dataset: d as any
            } as MeasurementDataset))
        };
        this.stateMessage = (value.datasets || []).some(d => d?.dataset?.data?.length > 0)
            ? '' : this.translate('no data available for this period');
        this.chartDataProvider.emit(value);
    }

    private datasetsForLocation(openingHours: EnergySignatureResult): ChartDatasetExtended[] {
        const colorOpen = AppContext.getChartColorForMeasurementAsString(DataType.gasConsumption);
        const colorClosed = AppContext.getChartColorForMeasurementAsString(DataType.heatConsumption);
        const openingHoursDataset = <ChartDatasetExtended>{
            label: this.translate('opening hours'),
            pointRadius: 2,
            pointHoverRadius: 4,
            pointBorderWidth: 0.5,
            pointStyle: 'circle',
            showLine: false,
            backgroundColor: colorOpen,
            pointBorderColor: '#ffffff',
            data: toEnergyProfileScatterPoints(openingHours.openingHours),
            tooltip: {}
        };
        const outsideOpeningHoursDataset = <ChartDatasetExtended>{
            label: this.translate('outside opening hours'),
            pointRadius: 2,
            pointHoverRadius: 4,
            pointBorderWidth: 0.5,
            pointStyle: 'circle',
            showLine: false,
            backgroundColor: colorClosed,
            pointBorderColor: '#ffffff',
            data: toEnergyProfileScatterPoints(openingHours.outsideOpeningHours),
            tooltip: {}
        };

        let visibleScatterDatasets: ChartDatasetExtended[];
        if (this.showOpeningHoursSplit) {
            visibleScatterDatasets = [openingHoursDataset, outsideOpeningHoursDataset];
        } else if (this.showWeekendWeekdaySplit) {
            const allPoints = [...openingHoursDataset.data as any[], ...outsideOpeningHoursDataset.data as any[]];
            visibleScatterDatasets = [
                {
                    ...openingHoursDataset,
                    label: this.translate('weekdays'),
                    data: allPoints.filter(point => !this.isWeekend(point))
                } as ChartDatasetExtended,
                {
                    ...outsideOpeningHoursDataset,
                    label: this.translate('weekend'),
                    data: allPoints.filter(point => this.isWeekend(point))
                } as ChartDatasetExtended
            ];
        } else {
            visibleScatterDatasets = [{ ...openingHoursDataset,
                label: this.translate('consumption'),
                data: [...openingHoursDataset.data as any[], ...outsideOpeningHoursDataset.data as any[]],
            } as ChartDatasetExtended];
        }
        const regressionDataset = this.createRegressionDataset(visibleScatterDatasets);
        return regressionDataset ? [...visibleScatterDatasets, regressionDataset] : visibleScatterDatasets;
    }

    private isWeekend(point: {label?: string}): boolean {
        const timestamp = moment(point.label);
        return timestamp.isValid() && [0, 6].includes(timestamp.day());
    }

    private createRegressionDataset(datasets: ChartDatasetExtended[]): ChartDatasetExtended | null {
        const points = datasets.flatMap(dataset => (dataset.data || []) as EnergyProfileScatterPoint[]);
        if (points.length < 2) {
            return null;
        }
        const meanX = points.reduce((sum, point) => sum + point.x, 0) / points.length;
        const meanY = points.reduce((sum, point) => sum + point.y, 0) / points.length;
        const numerator = points.reduce((sum, point) => sum + ((point.x - meanX) * (point.y - meanY)), 0);
        const denominator = points.reduce((sum, point) => sum + Math.pow(point.x - meanX, 2), 0);
        if (!Number.isFinite(denominator) || denominator === 0) {
            return null;
        }
        const slope = numerator / denominator;
        const intercept = meanY - (slope * meanX);
        const xValues = points.map(point => point.x);
        const minX = Math.min(...xValues);
        const maxX = Math.max(...xValues);
        if (!Number.isFinite(minX) || !Number.isFinite(maxX) || minX === maxX) {
            return null;
        }
        return {
            type: 'line',
            data: [
                {x: minX, y: Math.max(0, intercept + (slope * minX))},
                {x: maxX, y: Math.max(0, intercept + (slope * maxX))}
            ] as any,
            borderColor: '#5b7db1',
            borderWidth: 2,
            borderDash: [6, 4],
            pointRadius: 0,
            pointHoverRadius: 0,
            pointHitRadius: 0,
            fill: false,
            showLine: true,
            order: -1
        } as ChartDatasetExtended;
    }

    private formatTooltipTitle(point: EnergyProfileScatterPoint): string {
        const timestamp = moment(point?.label);
        if (!timestamp.isValid()) {
            return '';
        }
        if (this.displayResolution === TimeResolution.hour) {
            return timestamp.format('D MMM HH:mm');
        }
        return timestamp.format(point.sourceResolution === TimeResolution.month ? 'MMM YYYY' : 'D MMM YYYY');
    }

    private formatValue(value: number): string {
        return new Intl.NumberFormat(undefined, {maximumFractionDigits: 1})
            .format(compactEnergyProfileValue(value));
    }

    private translate(key: string) {
        return TranslateDirective.getTranslation(key, true);
    }

    private refreshOpeningHoursAvailability(): void {
        const info = this.location?.info as any;
        this.openingHoursAvailable = (info?.openingHours?.length ?? 0) > 0
            || (info?.closedOpeningDays?.length ?? 0) > 0;
        if (this.openingHoursAvailable || !info?.buildingType) {
            return;
        }
        this.sendQuery('getBuildingType', info.buildingType).subscribe(buildingType => {
            if (this.location?.info?.buildingType === buildingType?.buildingTypeId) {
                this.openingHoursAvailable = (buildingType?.info?.defaultOpeningHours?.length ?? 0) > 0;
            }
        });
    }
}
